import formatDate from '../utils/format-date';
import { query as $, scrollIntoView } from '../utils/dom';
import Component from './component';
import SearchComponent from './search';
import selectors from '../selectors';
import iconNext from '../assets/icons/next.svg';
import iconPrevious from '../assets/icons/previous.svg';
import iconSpinner from '../assets/icons/spinner.svg';
import { filter as searchFilter, result as searchResult } from '../utils/search';

/**
 * Render a playlist item template
 *
 * @param {Object} data
 */
function playlistItem (data) {
  return `
    <li tabindex="0" aria-label="${data.metadata.title}" title="Select playlist item" class="iono-playlist-item iono-padding-2 iono-flex iono-align-items-center iono-theme-background-dark@hover">
      <div class="iono-playlist-metadata iono-flex iono-flex-vertical iono-flex-grow">
        <div class="iono-playlist-metadata-title iono-text-ellipsis"></div>
        <div class="iono-text-small iono-theme-text-muted iono-text-ellipsis iono-playlist-metadata-subtitle"></div>
      </div>
      <div class="iono-playlist-duration iono-margin-left-2 iono-text-small iono-theme-text-muted" hidden></div>
    </li>
  `;
}

export default class PlaylistComponent extends Component {
  constructor (props) {
    super(props);

    this.search = new SearchComponent({
      filter: searchFilter(),
      result: searchResult(),
      onSelect: item => {
        this.props.player.selectPlaylistItem(item.index);
        this.props.player.play();
      }
    });

    this.handleEvent = this.handleEvent.bind(this);
  }

  getDefaultProps (props) {
    return {
      playlistIndex: -1,
      playlist: [],
      enableLoadMore: false,
      loading: false
    };
  }

  /**
   * Handle playback and ui events
   *
   * @param {Object} event
   */
  handleEvent (event) {
    if (event.type === 'repeatchange') {
      return this.updateNextPrevButtons();
    }

    if (event.type === 'playlistload') {
      this.props.loading = true;
      return this.updateLoadMore();
    }

    if (event.type === 'durationchange') {
      return this.updatePlaylistItem(this.props.player.getPlaylistIndex(), this.props.player.getPlaylistItem());
    }

    if (event.type === 'playlistchange') {
      this.props.enableLoadMore = this.props.player.canLoadMore();
      this.props.loading = false;
      return this.updatePlaylist(this.props.player.getPlaylist());
    }

    if (event.type === 'playlistselect') {
      return this.updatePlaylistIndex(this.props.player.getPlaylistIndex());
    }

    if (event.type === 'click' && event.target === this.$loadMore) {
      event.preventDefault();
      this.props.player.loadMore();
      return;
    }

    const playlistItem = event.target.closest('.iono-playlist-item');
    if (event.type === 'click' && playlistItem) {
      event.preventDefault();
      this.props.player.selectPlaylistItem(parseInt(playlistItem.dataset.index, 10));
      this.props.player.play();
      return;
    }

    const next = event.target.closest('.iono-playlist-next');
    if (event.type === 'click' && next) {
      event.preventDefault();
      this.props.player.nextPlaylistItem();

      const index = this.props.player.getPlaylistIndex();
      const $playlistItem = this.$(`.iono-playlist-item[data-index="${index}"]`);

      if ($playlistItem) scrollIntoView($playlistItem);

      return;
    }

    const previous = event.target.closest('.iono-playlist-previous');
    if (event.type === 'click' && previous) {
      event.preventDefault();
      this.props.player.previousPlaylistItem();

      const index = this.props.player.getPlaylistIndex();
      const $playlistItem = this.$(`.iono-playlist-item[data-index="${index}"]`);

      if ($playlistItem) scrollIntoView($playlistItem);
    }
  }

  /**
   * Update the playlist
   *
   * @param {Array} playlist
   */
  updatePlaylist (playlist) {
    this.props.playlist = this.props.player.getPlaylist();
    this.props.playlistIndex = this.props.player.getPlaylistIndex();

    $(selectors.Items, this.$element).innerHTML = playlist.map(function buildPlaylistItem (item, index) {
      return playlistItem({
        metadata: item.metadata,
        selected: false,
        index: index,
        artwork: this.props.artwork,
        localization: this.props.player.locale
      });
    }, this).join('');

    this.props.playlist.forEach(function (item, index) {
      this.updatePlaylistItem(index);
    }, this);

    this.updateTitle();
    this.updateSearch();
    this.updateNextPrevButtons();
    this.updateLoadMore();
  }

  updateTitle () {
    const metadataTitle = this.props.player.getPlaylistMetadata().title;
    this.$title.textContent = metadataTitle || this.props.player.locale.playlist.title;
  }

  updateSearch () {
    this.search.setProps({
      items: this.props.playlist.map(function (item, index) {
        const searchItem = {
          uid: item.metadata.uid,
          index: index,
          title: item.metadata.title
        };
        // override the toString method so that the title is used instead
        searchItem.toString = function () {
          return item.metadata.title;
        };
        return searchItem;
      })
    });
  }

  /**
   * Update the load more ui
   */
  updateLoadMore () {
    if (this.props.enableLoadMore) {
      this.$loadMore.removeAttribute('hidden');
    } else {
      this.$loadMore.setAttribute('hidden', true);
    }

    if (this.props.loading) {
      this.$loadMore.setAttribute('disabled', true);
      this.$loadMore.querySelector('.iono-icon').removeAttribute('hidden');
    } else {
      this.$loadMore.removeAttribute('disabled');
      this.$loadMore.querySelector('.iono-icon').setAttribute('hidden', true);
    }
  }

  /**
   * Update the selected playlist item
   *
   * @param {Number} index
   */
  updatePlaylistIndex (index) {
    this.props.playlistIndex = index;

    const previous = $(`${selectors.Item}${selectors.Selected}`, this.$element);
    if (previous) {
      previous.classList.remove('iono-selected');
    }

    const current = $(`${selectors.Item}[data-index="${index}"]`, this.$element);
    if (current) {
      current.classList.add('iono-selected');
    }

    this.updateNextPrevButtons();
  }

  /**
   * Update an individual playlist item
   *
   * @param  {number} index The index of the playlist item to update
   */
  updatePlaylistItem (index) {
    const current = this.$('.iono-playlist-items').children[index];

    if (!current) return;

    const playlistItem = this.props.playlist[index];
    const subtitle = [];

    if (playlistItem.metadata.publishDate instanceof Date) {
      subtitle.push(`<span class="iono-playlist-metadata-publish-date iono-text-uppercase">${formatDate(playlistItem.metadata.publishDate, this.props.dateTimeFormat, this.props.player.locale)}</span>`);
    }

    if (playlistItem.metadata.grouping) {
      subtitle.push(`<span class="iono-playlist-metadata-grouping">${playlistItem.metadata.grouping}</span>`);
    }

    current.dataset.index = index;

    if (index === this.props.playlistIndex) {
      current.classList.add('iono-selected');
    } else {
      current.classList.remove('iono-selected');
    }

    $('.iono-playlist-metadata-title', current).textContent = playlistItem.metadata.title;
    $('.iono-playlist-metadata-subtitle', current).innerHTML = subtitle.join(' / ');

    if (playlistItem.metadata.duration <= 0) {
      $('.iono-playlist-duration', current).setAttribute('hidden', true);
    } else {
      $('.iono-playlist-duration', current).textContent = iono.Player.formatSeconds(playlistItem.metadata.duration);
      $('.iono-playlist-duration', current).removeAttribute('hidden');
    }
  }

  /**
   * Update the next / previous buttons
   */
  updateNextPrevButtons () {
    if (this.$previous) {
      if (this.props.player.getRepeat() === 'playlist' || this.props.playlistIndex > 0) {
        this.$previous.removeAttribute('hidden');
      } else {
        this.$previous.setAttribute('hidden', true);
      }
    }

    if (this.$next) {
      if (this.props.player.getRepeat() === 'playlist' || (this.props.playlistIndex >= 0 && this.props.playlistIndex < (this.props.playlist.length - 1))) {
        this.$next.removeAttribute('hidden');
      } else {
        this.$next.setAttribute('hidden', true);
      }
    }
  }

  /**
   * Attach component to the DOM
   *
   * @param {Element} element
   */
  attach (element) {
    super.attach(element);

    this.$next = this.$('.iono-playlist-next');
    this.$previous = this.$('.iono-playlist-previous');
    this.$loadMore = this.$('.iono-playlist-load-more');
    this.$search = this.$('.iono-search');
    this.$title = this.$('.iono-playlist-header .iono-playlist-title');

    this.search.attach(this.$search);

    this.$element.addEventListener('click', this);

    this.props.player.on('playlistload', this.handleEvent);
    this.props.player.on('playlistselect', this.handleEvent);
    this.props.player.on('playlistchange', this.handleEvent);
    this.props.player.on('durationchange', this.handleEvent);
    this.props.player.on('repeatchange', this.handleEvent);

    this.updateNextPrevButtons();
  }

  /**
   * Detach component from the DOM
   *
   * @param {Element} element
   */
  detach (element) {
    if (this.$element) {
      this.$next = null;
      this.$previous = null;
      this.$loadMore = null;
      this.$title = null;
      this.$element.removeEventListener('click', this);
    }

    super.detach(element);

    this.search.detach();

    this.props.player.off('playlistload', this.handleEvent);
    this.props.player.off('playlistselect', this.handleEvent);
    this.props.player.off('playlistchange', this.handleEvent);
    this.props.player.off('durationchange', this.handleEvent);
    this.props.player.off('repeatchange', this.handleEvent);
  }

  render () {
    return `
      <div class="iono-playlist iono-flex iono-flex-vertical">
        <div class="iono-playlist-header iono-flex iono-align-items-center iono-flex-shrink-0 iono-padding-2">
          <div class="iono-playlist-title iono-flex-grow">${this.props.locale.playlist.title}</div>

          ${this.search.render()}

          <button class="iono-button iono-theme-button iono-playlist-previous iono-margin-left" title="${this.props.locale.playlist.previous}" hidden>
            <span class="iono-icon iono-icon-10 iono-button-icon" aria-hidden="true">${iconPrevious}</span>
            <span class="iono-button-label iono-sr-only">${this.props.locale.playlist.previous}</span>
          </button>

          <button class="iono-button iono-theme-button iono-playlist-next iono-margin-left" title="${this.props.locale.playlist.next}" hidden>
            <span class="iono-icon iono-icon-10 iono-button-icon" aria-hidden="true">${iconNext}</span>
            <span class="iono-button-label iono-sr-only">${this.props.locale.playlist.next}</span>
          </button>
        </div>
        <ul class="iono-playlist-items iono-border-top iono-flex-shrink-0"></ul>
        <button class="iono-button iono-theme-button iono-playlist-load-more" title="${this.props.locale.playlist.load_more}" ${this.props.enableLoadMore ? '' : 'hidden'}>
          <span class="iono-icon iono-button-icon iono-spin iono-margin-right" ${this.props.loading ? '' : 'hidden'} aria-hidden="true">${iconSpinner}</span>
          <span class="iono-button-label">${this.props.locale.playlist.load_more}</span>
        </button>
      </div>
    `;
  }
}
